import { useMemo, useRef, useState } from 'react'
import { Button, Col, Form, Input, Menu, Modal, Row, Table as T, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import userApi from 'src/api/userApi'
import { useGetData } from 'src/hooks/useGetData'
import { getFullName, hidePhone, isPastTimeV2 } from 'src/libs/util'
import { CollapseBlock } from 'src/components/common/other/CollapseBlock'
import { useModalState } from 'src/hooks/useModalState'
import { CustomModal } from 'src/components/modal'
import sessionApi from 'src/api/sessionApi'
import { TextLinkUserName } from 'src/components/common/other/TextLinkUserName'
import { UsernameById1 } from 'src/components/common/other/UsernameById'
import useGetWithContext from 'src/hooks/useGetWithContext'
import { DeleteOutlined } from '@ant-design/icons'
import { RateStudent } from 'src/components/class/demo/edit/RateStudent'
import { useGetCategories as useGetCategoriesV2 } from 'src/hooks/useGetCategoriesV2'
import { CATEGORY_TYPE, URL_TEMPLATE } from 'src/libs/constant'
import WithErrorBoundary from 'src/components/fallback/ErrorBoundaryV2'
import statisticReportApi from 'src/api/statisticsReport'
import { TagNewStudent } from 'src/components/common/other/TagNewStudent'
import { TagStudentType } from 'src/components/common/other/TagStudentType'
import { TicketFormHTKT } from 'src/components/ticket/monitor/list/TicketFormHTKT'
import { SCHEDULE_TYPE } from 'src/libs/constant/calendar'
import { useGetTechnicalTicketByStudent } from 'src/components/class/demo/edit/hooks'
import { SimpleNotesHtkt } from 'src/components/common/other/SimpleNotesHtkt'
import ActionContainer from 'src/components/session/teaching-schedule/ActionContainer'
import ActionButton from 'src/components/common/button/ActionButton'

export function StudentsInfo ({ classData, handleReload, validationValues, dataStudents }) {
  const { t } = useTranslation()

  const { getCategoryData: getTimeSlot } = useGetCategoriesV2(CATEGORY_TYPE.CLASS_TIME_SLOT)
  const timeSlot = getTimeSlot(classData?.classTimeSlotId)
  const isSessionStarted = isPastTimeV2({ date: classData?.learnDate, time: timeSlot?.startTime })

  const props = { classData, handleReload, validationValues, isSessionStarted, dataStudents }
  return (
    <CollapseBlock header={t('page.class.label.student_info')}>
      <StudentsTable {...props} />
    </CollapseBlock>
  )
}

function StudentsTable ({ classData, handleReload, validationValues, isSessionStarted, dataStudents }) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [studentIdx, setStudentIdx] = useState()
  const [dateNow, setDateNow] = useState(Date.now())
  const { handleClose, handleOpen, isModalVisible } = useModalState()

  const onCancel = () => {
    form.setFieldsValue({ ...form.getFieldValue(), comment: null })
    handleClose()
  }
  const addComment = (studentIdx) => {
    setStudentIdx(studentIdx)
    handleOpen()
  }
  const handleAddComment = async () => {
    const comment = form.getFieldValue('comment')
    if (!comment) return
    const response = await sessionApi.addStudentNote({
      id: classData.id,
      studentId: studentIdx,
      note: comment
    })
    if (response.status === 200) {
      handleReload()
    }
    onCancel()
  }

  const { isMakupSession, canEditSession } = validationValues

  const { data } = useGetData(sessionApi.studyProgramOfSession, classData?.studyProgramId, { isDisabled: !classData?.studyProgramId })
  const maxStudentInClass = data?.maxStudentInClass

  const studentsFilterRemoved = classData?.students?.filter(student => student.status !== 'REMOVED_STUDENT')
  const getStudentById = (studentId) => {
    if (!studentId || !dataStudents?.length) return null
    return dataStudents.find(item => item.studentId === studentId)
  }

  const columns = useMemo(() =>
    [
      // {
      //   title: t('page.class.label.stt'),
      //   key: 'stt',
      //   dataIndex: 'stt',
      //   render (_, record, index) {
      //     return <>{index + 1}</>
      //   },
      //   width: '60px'
      // },
      // {
      //   title: t('page.class.label.student_id'),
      //   dataIndex: 'student_id',
      //   key: 'student_id',
      //   render (_, record) {
      //     return <span>{record?.studentId}</span>
      //   }
      // },
      {
        title: 'Học sinh',
        dataIndex: 'fullNameStudent',
        key: 'fullNameStudent',
        render (_, record) {
          return (
            <>
              <TextLinkUserName
                userName={<UsernameById1 id={record?.studentId} fullName useHook={useGetWithContext} />}
                userId={record?.studentId}
              />
              <TagStudentType studentId={record?.studentId} learnDate={classData?.learnDate} />
              <TagNewStudent studentId={record?.studentId} sessionId={record?.classRegularSessionId} />
              <span className='d-block'>ID: {record?.studentId}</span>
            </>
          )
        }
      },
      // {
      //   title: t('page.class.label.phone'),
      //   dataIndex: 'phone',
      //   key: 'phone',
      //   render (_, record) {
      //     return <PhoneStudent student={record} />
      //   }
      // },
      {
        title: 'Phụ Huynh',
        dataIndex: 'parent',
        key: 'parent',
        render (_, record) {
          return (
            <>
              <Parent student={record} />
              <PhoneStudent student={record} />
            </>
          )
        }
      },
      {
        title: t('page.class.label.status'),
        dataIndex: 'status',
        key: 'status',
        render (_, record) {
          return <>{record?.status && t(`constant.student_regular_status.${record?.status}`)}</>
        }
      },
      {
        title: t('page.class.label.teacher_feedback'),
        dataIndex: 'teacher_feedback',
        key: 'teacher_feedback',
        width: '180px',
        render (_, record) {
          return (
            <WithErrorBoundary boundaryTag='components.session.edit.StudentsInfo'>
              <RateStudent studentId={record?.studentId} teacher={classData?.teacherId} code={classData?.code} diamond={record?.diamond} showDiamond/>
            </WithErrorBoundary>
          )
        }
      },
      {
        title: 'Hành động can thiệp',
        dataIndex: 'action',
        key: 'action',
        width: '500px',
        render (_, record) {
          const { studentId } = record
          const dataStudent = getStudentById(studentId) || null
          const { actions = [] } = dataStudent || {}
          return (
            <ActionContainer actions={actions} learnDate={classData?.learnDate} />
          )
        }
      },
      {
        title: t('page.class.label.note'),
        dataIndex: 'note',
        key: 'note',
        render (_, record) {
          const { studentId, classRegularSessionId } = record
          const dataStudent = getStudentById(studentId) || null
          const noteWarning = dataStudent?.noteWarning ? dataStudent.noteWarning.split(';') : null
          const { technicalTickets } = useGetTechnicalTicketByStudent(
            classRegularSessionId,
            studentId,
            { dependency: dateNow }
          )
          let notes = record?.notes || []
          technicalTickets?.forEach?.((technicalTicket) => {
            if (technicalTicket?.notes?.length) {
              technicalTicket?.notes.forEach((note) => {
                notes = notes.concat([{
                  ...note,
                  errorType: technicalTicket?.errorType,
                  status: technicalTicket?.supportStatus
                }])
              })
            }
          })
          return (
            <WithErrorBoundary boundaryTag='components.session.edit.StudentsInfo'>
              <Note notes={notes} />
              <div>
                {
                  noteWarning && noteWarning.map((item, idx) => (
                    <span
                      className='d-block'
                      key={idx}
                      style={{ color: '#ff0000' }}
                    >
                  {item}
                </span>
                  ))
                }
              </div>
            </WithErrorBoundary>
          )
        },
        width: '300px'
      },
      {
        title: t('page.class.label.action'),
        dataIndex: 'action',
        key: 'action',
        render (_, record) {
          return (
            <ActionDetail
              record={record}
              classData={classData}
              addComment={addComment}
              setDateNow={setDateNow}
            />
            // <>
            //   {/* mở lại sau */}
            //   {/* {isMakupSession && canEditSession && studentsFilterRemoved?.length > 1 && !isSessionStarted && <DeleteButtons student={record} handleReload={handleReload} sessionData={classData} />} */}
            //   <>
            //     {data && record.status === 'SUCCESS' &&
            //       <>
            //         {data?.status === 'PROCESSED' ?
            //           <Button type='primary' className='mb-2' style={{ width: 170 }}>
            //             <a className=' text-decoration-none'
            //                href={`${URL_TEMPLATE.STATISTICS_REPORT_DETAIL}?id=${data?.id}`}>
            //               {t('page.statistics_report.label.view_evaluate')}
            //             </a>
            //           </Button> :
            //           <Button type='primary' danger className='mb-2' style={{ width: 170 }}>
            //             <a className=' text-decoration-none'
            //                href={`${URL_TEMPLATE.STATISTICS_REPORT_DETAIL}?id=${data?.id}`}>
            //               {t('page.statistics_report.label.evaluate_now')}
            //             </a>
            //           </Button>
            //         } <br />
            //       </>
            //     }
            //   </>
            //   <Row>
            //     <Button variant='outlined' onClick={() => addComment(record?.studentId)} style={{ width: 170 }}
            //             type='primary'>
            //       {t('page.class.label.add_comment')}
            //     </Button>
            //   </Row>
            //   <TicketFormHTKT studentInfo={studentInfo} handleReload={() => {}} isButton setDateNow={setDateNow}
            //                   typeButton='text' />
            // </>
          )
        }
      }
    ], [classData, dataStudents, t])

  return (
    <WithErrorBoundary boundaryTag='components.session.edit.StudentsInfo'>
      <T dataSource={studentsFilterRemoved} columns={columns} pagination={false} rowKey='id'
         className='pb-3 lms-table-no-page' />
      <Modal
        onCancel={onCancel}
        title={t('page.class.label.add_comment')}
        visible={isModalVisible}
        footer={null}
      >
        <Form form={form} onFinish={handleAddComment}>
          <Form.Item
            name='comment'
            wrapperCol={{ span: 24 }}
            rules={[{ required: true, message: t('common.text.field_required') }]}
          >
            <Input.TextArea rows={4} placeholder={t('page.class.placeholder.input_comment')} />
          </Form.Item>
          <Row>
            <Col
              span={24}
              style={{
                textAlign: 'center'
              }}
            >
              <Button
                type='primary'
                htmlType='submit'
                className='mr-2'
              >
                {t('common.label.send_comment')}
              </Button>
              <Button onClick={handleClose}>
                {t('common.label.cancel')}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
      {/* mở lại sau */}
      {/* {isMakupSession && classData?.numberOfStudents < maxStudentInClass && !isSessionStarted && (
        <div className='px-3'>
          <AddStudentButton classData={classData} handleReload={handleReload} />
        </div>
      )} */}
    </WithErrorBoundary>
  )
}

const ActionDetail = ({ record, classData, addComment, setDateNow }) => {
  const { t } = useTranslation()
  const { data } = useGetWithContext(`GET_SESSION_BY_ID_${record?.classRegularSessionId}`, statisticReportApi.getDataEvaluateSessionId, record?.classRegularSessionId, { isDisabled: !record?.classRegularSessionId })
  const { studentId } = record
  const { data: dataStudent } = useGetData(userApi.detailStudent, studentId, {
    isDisabled: !studentId
  })

  const studentInfo = {
    studentName: dataStudent?.fullName,
    phone: dataStudent?.phone,
    sessionCode: classData?.code,
    classInId: classData?.classInId,
    type: SCHEDULE_TYPE.REGULAR,
    studentId,
    classTimeSlotId: classData?.classTimeSlotId
  }
  const menu = (
    <Menu className='menu-custom'>
      {data && record.status === 'SUCCESS' && (
        <Menu.Item key='review' >
          {data?.status === 'PROCESSED' ?
            <Button type='text' style={{ width: '100%' }}>
              <a className=' text-decoration-none'
                 href={`${URL_TEMPLATE.STATISTICS_REPORT_DETAIL}?id=${data?.id}`}>
                {t('page.statistics_report.label.view_evaluate')}
              </a>
            </Button> :
            <Button type='text' style={{ width: '100%' }}>
              <a className=' text-decoration-none'
                 href={`${URL_TEMPLATE.STATISTICS_REPORT_DETAIL}?id=${data?.id}`}>
                {t('page.statistics_report.label.evaluate_now')}
              </a>
            </Button>
          }
        </Menu.Item>
      )}
      <Menu.Item key='note'>
        <Button onClick={() => addComment(record?.studentId)} style={{ width: 170 }}
                type='text'>
          {t('page.class.label.add_comment')}
        </Button>
      </Menu.Item>
      <Menu.Item key='httk'>
        <TicketFormHTKT studentInfo={studentInfo} handleReload={() => {}} isButton setDateNow={setDateNow}
                        typeButton='text' />
      </Menu.Item>
    </Menu>
  )
  return (
    <div className='d-flex justify-content-center'>
      <ActionButton menu={menu} />
    </div>
  )
}

function PhoneStudent ({ student }) {
  const { data: studentDetail } = useGetData(userApi.detailStudent, student?.studentId, {
    isDisabled: !student?.studentId
  })
  if (studentDetail && studentDetail?.phone) {
    return <span>{hidePhone(studentDetail?.phone)}</span>
  }
  return null
}

function Parent ({ student }) {
  const { data: studentDetail } = useGetData(userApi.detailStudent, student?.studentId, {
    isDisabled: !student?.studentId
  })

  let parent = null

  if (studentDetail) {
    parent = getFullName(studentDetail?.parent)
  }

  if (!student) return null
  return <><span className='d-block'>{parent}</span></>
}

function Note ({ notes }) {
  const filterNotes = notes?.filter((item) => !!item?.comment)

  if (filterNotes?.length) {
    return (
      <div className='comment'>
        <SimpleNotesHtkt notes={filterNotes} />
      </div>
    )
  }
  return null
}

function DeleteButtons ({ handleReload, student, sessionData }) {
  const { t } = useTranslation()
  const [isSubmitting, setSubmitting] = useState(false)
  const modalState = useRef({})
  const { handleOpen, handleClose, isModalVisible } = useModalState()

  const handleClickRemove = () => {
    modalState.current = {
      confirmText: 'page.class.text.confirm_remove_student',
      request: async () => {
        const response = await sessionApi.deleteStudentInSession({
          classRegularSessionId: sessionData?.id,
          studentId: student?.studentId
        })
        if (response.status === 200) {
          handleClose()
          handleReload()
        }
      },
      title: t('page.class.label.remove_student')
    }
    handleOpen()
  }

  const { confirmText, request, title } = modalState.current
  const onOk = async () => {
    setSubmitting(true)
    await request()
    setSubmitting(false)
  }
  const { data: studentDetail } = useGetData(userApi.detailStudent, student?.studentId, { isDisabled: !student?.studentId })
  const studentName = studentDetail ? (
    <TextLinkUserName userName={getFullName(studentDetail)} userId={studentDetail.id} />) : (student?.studentId)
  const content = (
    <p>
      {t(confirmText)}
      <strong>{studentName}</strong>
    </p>
  )

  return (
    <>
      <Tooltip title={t('page.class.label.delete')}>
        <Button type='link' danger onClick={handleClickRemove}>
          <DeleteOutlined />
        </Button>
      </Tooltip>
      <CustomModal
        visible={isModalVisible}
        title={title}
        onCancel={handleClose}
        onOk={onOk}
        isSubmitting={isSubmitting}
        destroyOnClose
        okText={t('common.label.agree')}
      >
        {content}
      </CustomModal>
    </>
  )
}
